import { uniqueId } from 'utils/Numbers';
import { CustomSalaryComponent } from './types';
import { EarningsComponentType, SalaryComponent } from 'components/SalaryComponents/types';
import { ReviseSalaryFormData } from '../../constants';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { paiseAmountToRupee } from 'utils/Numbers';

export type ComponentSectionGroup = {
  fixedEarnings: CustomSalaryComponent[];
  variableEarnings: CustomSalaryComponent[];
  otherCtcComponents: CustomSalaryComponent[];
};

const FIXED_PAY_TYPE = 'fixed';
const VARIABLE_PAY_TYPE = 'variable';
const NON_PAYABLE_PAY_TYPE = 'non_payable_benefit';

export const groupComponentsBySection = (
  selectedComponents: CustomSalaryComponent[],
  allComponents: EarningsComponentType[],
): ComponentSectionGroup => {
  const fixedEarnings: CustomSalaryComponent[] = [];
  const variableEarnings: CustomSalaryComponent[] = [];
  const otherCtcComponents: CustomSalaryComponent[] = [];

  selectedComponents.forEach((comp) => {
    const fullComponent = allComponents.find((c) => c.id === comp.componentId);
    const payType = fullComponent?.settings?.earningDetails?.payType ?? FIXED_PAY_TYPE;

    if (payType === NON_PAYABLE_PAY_TYPE) {
      otherCtcComponents.push(comp);
    } else if (payType === VARIABLE_PAY_TYPE) {
      variableEarnings.push(comp);
    } else {
      fixedEarnings.push(comp);
    }
  });

  return {
    fixedEarnings: sortComponentsListByComponentType({ components: fixedEarnings }),
    variableEarnings: sortComponentsListByComponentType({
      components: variableEarnings,
    }),
    otherCtcComponents: sortComponentsListByComponentType({
      components: otherCtcComponents,
    }),
  };
};

export const salaryComponentToCustomSalaryComponent = (
  component: SalaryComponent,
): CustomSalaryComponent => {
  return {
    id: uniqueId(),
    componentId: component.id,
    name: component.name,
    amount: '',
    isPredefined: component.isPredefined ?? false,
    category: component.category,
    componentType: component.componentType,
    componentSource:
      'componentSource' in component
        ? component.componentSource
        : 'COMPONENT_SOURCE_SALARY_STRUCTURE',
  };
};

export const sortComponentsListByComponentType = ({
  components,
}: {
  components: CustomSalaryComponent[];
}) => {
  return [...components].sort((component1, component2) => {
    if (component1.componentType === 'COMPONENT_TYPE_BASIC') return -1;
    if (component2.componentType === 'COMPONENT_TYPE_BASIC') return 1;

    // Special Allowance comes last
    if (component1.componentType === 'COMPONENT_TYPE_SPECIAL_ALLOWANCE') return 1;
    if (component2.componentType === 'COMPONENT_TYPE_SPECIAL_ALLOWANCE') return -1;

    return 0;
  });
};

export const getInitialCustomComponents = ({
  exisitingCustomSalaryStructure,
  allNonAdhocComponents,
  formData,
}: {
  exisitingCustomSalaryStructure: CustomSalaryComponent[];
  allNonAdhocComponents: EarningsComponentType[];
  formData: Partial<ReviseSalaryFormData>;
}): CustomSalaryComponent[] => {
  if (formData.isCustomSalaryStructure && formData.customSalaryComponents?.length) {
    return formData.customSalaryComponents;
  }
  if (exisitingCustomSalaryStructure?.length) {
    return exisitingCustomSalaryStructure;
  }
  return allNonAdhocComponents
    .filter((comp) => comp.isPredefined)
    .map((comp) => ({
      id: uniqueId(), //row id for uniqueness
      componentId: comp.id,
      name: comp.name,
      amount: '0',
      isPredefined: comp.isPredefined ?? false,
      category: comp.category,
      componentType: comp.componentType,
      componentSource: comp.componentSource,
    }));
};

export const getComponentDescription = (component: EarningsComponentType) => {
  if (component.componentType === 'COMPONENT_TYPE_NON_PAYABLE_BENEFIT') {
    return 'Non-Payable Benefit';
  }
  return `Earning (${
    component.componentType === 'COMPONENT_TYPE_VARIABLE' ? 'Variable' : 'Monthly'
  })`;
};

export const validateCustomSalaryStructure = (
  components: CustomSalaryComponent[],
  customCtc?: number,
) => {
  const hasError = components.some(
    (component) =>
      component.componentType !== 'COMPONENT_TYPE_SPECIAL_ALLOWANCE' &&
      (!component.componentId || !component.amount || component.amount === '0'),
  );
  if (hasError) {
    return 'Please ensure all salary components have a valid amount greater than 0';
  }

  if (customCtc) {
    const nonSASum = components
      .filter((c) => c.componentType !== 'COMPONENT_TYPE_SPECIAL_ALLOWANCE')
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    if (nonSASum > customCtc) {
      return `Sum of component values (₹${nonSASum.toLocaleString(
        'en-IN',
      )}) exceeds Annual CTC (₹${customCtc.toLocaleString('en-IN')})`;
    }
  }

  return null;
};

export const transformSalaryStructureComponentsToCustomComponents = (
  structureComponents: SalaryStructureComponent[],
): CustomSalaryComponent[] => {
  return structureComponents.map((component) => {
    return {
      id: uniqueId(),
      componentId: component.salaryComponent.id,
      name: component.salaryComponent.name,
      amount: paiseAmountToRupee(Number(component.formula ?? 0)).toString(),
      isPredefined: component.salaryComponent.isPredefined ?? false,
      category: component.salaryComponent.category,
      componentType: component.salaryComponent.componentType,
      componentSource:
        'componentSource' in component.salaryComponent
          ? component.salaryComponent.componentSource
          : 'COMPONENT_SOURCE_SALARY_STRUCTURE',
    };
  });
};
